import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  CheckCircle2,
  ExternalLink,
  FileUp,
  MailX,
  Package,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import type {
  EnhancedDeal,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierInvite,
  SupplierOrg,
  TimberProject,
} from '../../types';
import type { PurchaseRequisition } from '../../types/purchasing';
import { useAuth } from '../../utils/AuthContext';
import { getUserInitials } from '../../utils/avatarUtils';
import { canUploadSupplierQuoteOnBehalf } from '../../utils/staffSupplierQuoteAccess';
import { useCRM } from '../../utils/CRMContext';
import {
  canOverridePurchaseApproval,
  canReviewPurchaseRequisition,
} from '../../utils/purchasingAccess';
import {
  canActOnPendingPurchaseStep,
  crmUsersToResolverUsers,
  currentPendingStep,
  overlaySignedInApprover,
  purchaseApprovalUsesSubstitutedActor,
} from '../../utils/purchasingApprovals';
import { isAchPurchasePayment } from '../../utils/purchasingPolicy';
import {
  approvePurchaseRequisition,
  returnPurchaseRequisition,
  subscribePurchaseRequisition,
} from '../../utils/purchasingRequisitions';
import { isAwaitingReviewer, PURCHASE_STATUS_LABEL } from '../../utils/purchasingStatus';
import { jobPlBucketLabel } from '../../utils/purchasingSpendClass';
import { otherQuotePayMethodLabel } from '../../utils/otherQuotePurchase';
import { rfqProjectSupplierOrgIds } from '../../utils/rfqProjectSuppliers';
import {
  approvedSubmissionIdsForReleaseSelection,
  buildOtherQuoteApprovalUpdate,
  filterPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
} from '../../utils/timberRfqPrePortalVersion';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { formatOtherQuoteLabel } from '../../utils/otherQuoteUpload';
import { resolveOtherQuoteSupplierName } from '../../utils/otherQuoteVendor';
import { isValidTimestamp } from '../../utils/leadsHubUtils';
import {
  buildQuoteRejectionTargets,
  isRfqQuoteRejected,
  type QuoteRejectionTarget,
} from '../../utils/rfqQuoteRejectionTargets';
import {
  recordSupplierBillForRfqAward,
  retractSupplierBillForRfqAward,
} from '../../utils/supplierBillIngestion';
import {
  findSupplierBillForPurchaseOrder,
  findSupplierBillForRfqAward,
  setSupplierBillPaymentTerms,
} from '../../utils/supplierBillsService';
import type { SupplierPaymentTermsSpec } from '../../utils/supplierBills';
import SupplierPaymentTermsModal from './SupplierPaymentTermsModal';
import SupplierQuoteRejectionModal from './SupplierQuoteRejectionModal';
import OtherQuoteUploadModal from './OtherQuoteUploadModal';
import OtherQuoteApprovalModal, {
  type OtherQuoteApprovalTarget,
} from './OtherQuoteApprovalModal';
import RfqQuoteDeleteConfirmModal from './RfqQuoteDeleteConfirmModal';
import { useSupplierBills } from '../../hooks/useSupplierBills';
import {
  buildApprovedQuotePaymentTermsTarget,
  findAwardBillForSubmission,
  resolveApprovedQuoteBannerMeta,
  timberQuoteApprovedAt,
  timberQuoteApproverInitials,
} from '../../utils/approvedQuoteBanner';
import ApprovedQuoteBanner from './ApprovedQuoteBanner';
import './TimberRfqSuppliersPanel.css';
import './OtherQuotesPanel.css';

export type OtherQuotesPanelProps = {
  deal: EnhancedDeal;
  timberProject?: TimberProject | null;
  project: RfqProject | null;
  packages: RfqPackage[];
  submissions: RfqSubmission[];
  supplierOrgById: Map<string, SupplierOrg>;
  supplierInvites: SupplierInvite[];
  hubProjectManagerUid?: string;
  projectNumber?: string;
  projectName?: string;
  onUpdateProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  onOpenSubmission: (sub: RfqSubmission) => void;
  onDeleteSubmission?: (sub: RfqSubmission, project: RfqProject) => void | Promise<void>;
};

function activeQuoteSubmissions(subs: RfqSubmission[]): RfqSubmission[] {
  return subs
    .filter((s) => !s.notBidding && (s.storagePath || s.cncQuote))
    .sort((a, b) => {
      const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return bTime - aTime;
    });
}

const OtherQuotesPanel: React.FC<OtherQuotesPanelProps> = ({
  deal,
  timberProject,
  project,
  packages,
  submissions,
  supplierOrgById,
  supplierInvites,
  hubProjectManagerUid,
  projectNumber,
  projectName,
  onUpdateProject,
  onOpenSubmission,
  onDeleteSubmission,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const supplierBills = useSupplierBills();
  const crmUser = crmUsers.find((u) => u.uid === user?.id);
  const accessArgs = {
    authRole: user?.role,
    designations: crmUser?.designations,
    briefingProfile: crmUser?.executiveBriefing?.enabled
      ? crmUser.executiveBriefing.profile
      : crmUser?.executiveBriefing?.profile,
  };
  const canOverride = canOverridePurchaseApproval(accessArgs);
  const purchaseResolverUsers = useMemo(() => {
    const base = crmUsersToResolverUsers(crmUsers);
    if (!user?.id) return base;
    return overlaySignedInApprover(base, {
      uid: user.id,
      displayName: user.name || user.email || 'Staff',
      role: user.role,
      designations: crmUser?.designations,
      executiveBriefing: crmUser?.executiveBriefing,
      reportsToUserId: crmUser?.reportsToUserId,
      isActive: crmUser?.isActive,
    });
  }, [crmUsers, user, crmUser]);

  const canStaffUploadQuote = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canUploadSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [approvalTarget, setApprovalTarget] = useState<OtherQuoteApprovalTarget | null>(null);
  const [approveBusyId, setApproveBusyId] = useState<string | null>(null);
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    sub: RfqSubmission;
    orgName: string;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [linkedPos, setLinkedPos] = useState<Record<string, PurchaseRequisition | null>>({});
  const [paymentTermsTarget, setPaymentTermsTarget] = useState<{
    submissionKey: string;
    vendorName: string;
    quoteLabel: string;
    amount: number;
    purchaseRequisitionId?: string;
    initialTerms?: SupplierPaymentTermsSpec | null;
    lockedInstallmentIds?: string[];
    paidInstallmentIds?: string[];
  } | null>(null);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionModalTargets, setRejectionModalTargets] = useState<QuoteRejectionTarget[] | null>(
    null,
  );
  const rejectionAfterCloseRef = useRef<(() => void) | null>(null);

  const projectId = project?.id;
  const releaseId = PRE_PORTAL_RELEASE_ID;

  const projectPackages = useMemo(() => {
    if (!projectId) return [];
    return packages.filter((p) => p.rfqProjectId === projectId);
  }, [packages, projectId]);

  const submissionsByPackageId = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    if (!projectId) return m;
    submissions
      .filter((s) => s.rfqProjectId === projectId)
      .forEach((s) => {
        const pid = s.rfqPackageId;
        if (!pid) return;
        const list = m.get(pid) ?? [];
        list.push(s);
        m.set(pid, list);
      });
    return m;
  }, [submissions, projectId]);

  const packagesForRelease = useMemo(
    () => filterPackagesForReleaseSelection(projectPackages, releaseId),
    [projectPackages, releaseId],
  );

  const projectSupplierOrgIds = useMemo(
    () => (project ? rfqProjectSupplierOrgIds(project) : []),
    [project],
  );

  const quoteRows = useMemo(() => {
    const rows: Array<{
      sub: RfqSubmission;
      orgId: string;
      orgName: string;
    }> = [];
    for (const pkg of packagesForRelease) {
      if (!pkg.id || !pkg.supplierOrgId) continue;
      for (const sub of activeQuoteSubmissions(submissionsByPackageId.get(pkg.id) ?? [])) {
        const orgName = resolveOtherQuoteSupplierName(sub, pkg.supplierOrgId, supplierOrgById);
        rows.push({ sub, orgId: pkg.supplierOrgId, orgName });
      }
    }
    return rows.sort((a, b) => {
      const ta = a.sub.createdAt instanceof Date ? a.sub.createdAt.getTime() : 0;
      const tb = b.sub.createdAt instanceof Date ? b.sub.createdAt.getTime() : 0;
      return tb - ta;
    });
  }, [packagesForRelease, submissionsByPackageId, supplierOrgById]);

  const linkedPoIds = useMemo(
    () =>
      Array.from(
        new Set(quoteRows.map(row => row.sub.purchaseRequisitionId).filter((id): id is string => !!id)),
      ),
    [quoteRows],
  );
  const linkedPoKey = linkedPoIds.join('|');

  useEffect(() => {
    if (!linkedPoIds.length) {
      setLinkedPos({});
      return undefined;
    }
    const unsubs = linkedPoIds.map(id =>
      subscribePurchaseRequisition(id, row => {
        setLinkedPos(prev => (prev[id] === row ? prev : { ...prev, [id]: row }));
      }),
    );
    return () => unsubs.forEach(unsub => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribe to the current id set
  }, [linkedPoKey]);

  const approvedSubmissionIds = useMemo(() => {
    if (!project) return new Set<string>();
    return new Set(
      approvedSubmissionIdsForReleaseSelection(
        project,
        projectPackages,
        submissionsByPackageId,
        releaseId,
      ),
    );
  }, [project, projectPackages, submissionsByPackageId, releaseId]);

  const rejectionTargets = useMemo(() => {
    if (!project) return [];
    return buildQuoteRejectionTargets({
      workType: 'other_quote',
      project,
      projectSupplierOrgIds,
      packages: projectPackages,
      submissionsByPackageId,
      selectedReleaseId: releaseId,
      supplierOrgById,
    });
  }, [project, projectSupplierOrgIds, projectPackages, submissionsByPackageId, supplierOrgById]);

  const rejectionTargetOrgIds = useMemo(
    () => new Set(rejectionTargets.map((t) => t.orgId)),
    [rejectionTargets],
  );

  const openPaymentTermsAfterAward = (
    target: OtherQuoteApprovalTarget,
    opts?: { purchaseRequisitionId?: string; skipIfCard?: boolean },
  ) => {
    if (opts?.skipIfCard) return;
    const pendingTerms = {
      submissionKey: target.sub.id!,
      vendorName: target.orgName,
      quoteLabel: target.quoteLabel,
      amount: submissionQuoteTotal(target.sub) ?? 0,
      purchaseRequisitionId: opts?.purchaseRequisitionId,
      initialTerms: target.sub.paymentTermsDraft ?? null,
    };
    const nextProject = project
      ? {
          ...project,
          ...buildOtherQuoteApprovalUpdate(project, releaseId, target.sub.id!, true),
        }
      : project;
    const nextTargets = nextProject
      ? buildQuoteRejectionTargets({
          workType: 'other_quote',
          project: nextProject,
          projectSupplierOrgIds,
          packages: projectPackages,
          submissionsByPackageId,
          selectedReleaseId: releaseId,
          supplierOrgById,
        })
      : [];
    const openTerms = () => setPaymentTermsTarget(pendingTerms);
    if (nextTargets.length) {
      rejectionAfterCloseRef.current = openTerms;
      setRejectionModalTargets(nextTargets);
      setRejectionModalOpen(true);
    } else {
      openTerms();
    }
  };

  const approveLinkedPurchaseOrder = async (
    target: OtherQuoteApprovalTarget,
    input: { note?: string; paymentMethod?: string; paymentMethodId?: string },
  ) => {
    const requisition = target.sub.purchaseRequisitionId
      ? linkedPos[target.sub.purchaseRequisitionId]
      : null;
    if (!requisition || !user?.id) throw new Error('Purchase order is not available.');
    const pending = currentPendingStep(requisition.approvalSteps);
    const isFinal = !!pending && requisition.approvalSteps.filter(s => s.status === 'pending').length === 1;
    const approvalSubstitute = purchaseApprovalUsesSubstitutedActor(
      pending,
      user.id,
      purchaseResolverUsers,
      accessArgs,
    );
    setApproveBusyId(target.sub.id || requisition.id);
    try {
      await approvePurchaseRequisition({
        requisition,
        actorId: user.id,
        actorName: user.name || user.email || 'Staff',
        note: input.note,
        paymentMethod: input.paymentMethod,
        paymentMethodId: input.paymentMethodId,
        override: approvalSubstitute,
      });
      if (isFinal) {
        const method = {
          id: input.paymentMethodId || requisition.paymentMethodId,
          label: input.paymentMethod || requisition.paymentMethod,
        };
        openPaymentTermsAfterAward(target, {
          purchaseRequisitionId: requisition.id,
          skipIfCard: !isAchPurchasePayment(method),
        });
      }
    } finally {
      setApproveBusyId(null);
    }
  };

  const returnLinkedPurchaseOrder = async (target: OtherQuoteApprovalTarget, note: string) => {
    const requisition = target.sub.purchaseRequisitionId
      ? linkedPos[target.sub.purchaseRequisitionId]
      : null;
    if (!requisition || !user?.id) throw new Error('Purchase order is not available.');
    const pending = currentPendingStep(requisition.approvalSteps);
    const approvalSubstitute = purchaseApprovalUsesSubstitutedActor(
      pending,
      user.id,
      purchaseResolverUsers,
      accessArgs,
    );
    await returnPurchaseRequisition({
      requisition,
      actorId: user.id,
      actorName: user.name || user.email || 'Staff',
      note,
      override: approvalSubstitute,
    });
  };

  const approveOtherQuote = async (target: OtherQuoteApprovalTarget) => {
    const { sub, orgId, orgName, quoteLabel } = target;
    if (!project?.id || !sub.id) return;
    if (sub.purchaseRequisitionId) {
      await approveLinkedPurchaseOrder(target, {});
      return;
    }
    const approverInitials = getUserInitials(user?.name || user?.email || '');
    setApproveBusyId(sub.id);
    try {
      const awardUpdate = buildOtherQuoteApprovalUpdate(
        project,
        releaseId,
        sub.id,
        true,
        approverInitials,
      );
      await onUpdateProject(project.id, awardUpdate);
      try {
        await recordSupplierBillForRfqAward(
          {
            project,
            category: 'other',
            orgId,
            orgName,
            submissionKey: sub.id,
            amount: submissionQuoteTotal(sub),
            description: `Other quote — ${quoteLabel}`,
            byUserId: user?.id || '',
            byUserName: user?.name || user?.email || '',
            plBucket: sub.plBucket,
          },
          { throwOnError: true },
        );
        openPaymentTermsAfterAward(target);
      } catch (err) {
        throw new Error(
          err instanceof Error
            ? err.message
            : 'Quote was approved but the Accounts Payable bill could not be created.',
        );
      }
    } finally {
      setApproveBusyId(null);
    }
  };

  const handleApproveQuote = (sub: RfqSubmission, orgId: string, orgName: string) => {
    if (!project?.id || !sub.id) return;
    const scopeLabel = formatOtherQuoteLabel(sub);
    const total = submissionQuoteTotal(sub);
    const quoteLabel =
      total != null ? `${scopeLabel} — ${formatQuoteTotal(total)}` : scopeLabel;
    setApprovalTarget({
      sub,
      orgId,
      orgName,
      quoteLabel,
      scopeLabel,
      amount: total,
    });
  };

  const handleConfirmApproval = async () => {
    if (!approvalTarget) return;
    await approveOtherQuote(approvalTarget);
  };

  const handleSavePaymentTerms = async (terms: SupplierPaymentTermsSpec) => {
    if (!paymentTermsTarget || !project?.id) return;
    const bill = paymentTermsTarget.purchaseRequisitionId
      ? await findSupplierBillForPurchaseOrder(paymentTermsTarget.purchaseRequisitionId)
      : await findSupplierBillForRfqAward(project.id, paymentTermsTarget.submissionKey);
    if (!bill?.id) {
      throw new Error('Could not find the supplier bill for this quote.');
    }
    await setSupplierBillPaymentTerms(bill.id, terms, {
      expectedRfqSubmissionId: paymentTermsTarget.submissionKey,
      allowRaiseBillAmount: true,
    });
    setPaymentTermsTarget(null);
  };

  const requestDeleteSubmission = (sub: RfqSubmission, orgName: string) => {
    if (!onDeleteSubmission || !project || !sub.id) return;
    setDeleteError(null);
    setDeleteTarget({ sub, orgName });
  };

  const handleDeleteSubmission = async () => {
    if (!onDeleteSubmission || !project || !deleteTarget?.sub.id) return;
    setDeleteBusyId(deleteTarget.sub.id);
    setDeleteError(null);
    try {
      await onDeleteSubmission(deleteTarget.sub, project);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this quote.');
    } finally {
      setDeleteBusyId(null);
    }
  };

  const openRejectionModal = (targets: QuoteRejectionTarget[]) => {
    setRejectionModalTargets(targets);
    setRejectionModalOpen(true);
  };

  return (
    <section className="srfq-work-type-section other-quotes-panel" id="other_quote" aria-labelledby="other_quote-heading">
      <header className="srfq-work-type-section-head">
        <h3 id="other_quote-heading" className="srfq-work-type-section-title">
          <Package size={16} aria-hidden />
          Other Quotes
        </h3>
        <div className="srfq-work-type-section-meta">
          {quoteRows.length > 0 && (
            <span className="ops-stat ops-stat-done">
              {quoteRows.length} quote{quoteRows.length === 1 ? '' : 's'}
            </span>
          )}
          {rejectionTargets.length > 0 && (
            <button
              type="button"
              className="ops-view-btn srfq-rejection-notices-btn"
              onClick={() => openRejectionModal(rejectionTargets)}
            >
              Notify rejected suppliers ({rejectionTargets.length})
            </button>
          )}
          {canStaffUploadQuote && deal.id && (
            <button
              type="button"
              className="ops-view-btn"
              onClick={() => setUploadOpen(true)}
            >
              <FileUp size={13} aria-hidden />
              Upload quote
            </button>
          )}
        </div>
      </header>

      {quoteRows.length === 0 ? (
        <div className="srfq-work-type-empty">
          <Package size={24} strokeWidth={1.25} aria-hidden />
          <p>No other quotes yet</p>
          <p className="srfq-work-type-empty-hint">
            Staff-uploaded supplier quotes for work outside timber and CNC — hardware, fasteners,
            connectors, and similar.
            {canStaffUploadQuote ? ' Use Upload quote above to add a supplier PDF.' : ''}
          </p>
        </div>
      ) : (
        <div className="srfq-work-type-body other-quotes-panel-body">
          <ul className="srfq-timber-quote-rows other-quotes-list" aria-label="Other quotes">
            {quoteRows.map(({ sub, orgId, orgName }) => {
              const isApproved = !!sub.id && approvedSubmissionIds.has(sub.id);
              const isRejected = isRfqQuoteRejected(sub);
              const canReject = !!sub.id && !isRejected && rejectionTargetOrgIds.has(orgId);
              const label = formatOtherQuoteLabel(sub);
              const amountValue = submissionQuoteTotal(sub);
              const linkedPo = sub.purchaseRequisitionId ? linkedPos[sub.purchaseRequisitionId] : null;
              const payLabel =
                linkedPo?.paymentMethod
                || sub.paymentMethod
                || otherQuotePayMethodLabel(linkedPo?.paymentMethodId || sub.paymentMethodId);
              const nodeLabel = jobPlBucketLabel(linkedPo?.plBucket || sub.plBucket);
              const canReviewPo = !!(
                linkedPo
                && user?.id
                && (isAwaitingReviewer(linkedPo, user.id)
                  || canReviewPurchaseRequisition(accessArgs, linkedPo, user.id)
                  || canOverride)
              );
              const showLegacyApprove = !sub.purchaseRequisitionId && !isApproved && !isRejected;
              const showPoReview =
                !!linkedPo
                && !isRejected
                && (linkedPo.status === 'pending_approval' || linkedPo.status === 'in_review');
              const validUntil =
                sub.quoteValidUntil instanceof Date && isValidTimestamp(sub.quoteValidUntil)
                  ? format(sub.quoteValidUntil, 'MMM d, yyyy')
                  : null;

              return (
                <li
                  key={sub.id ?? sub.storagePath}
                  className={`srfq-timber-quote-row other-quotes-row${
                    isApproved ? ' approved' : isRejected ? ' rejected' : ' awaiting-approval'
                  }`}
                >
                  {isApproved && (
                    <ApprovedQuoteBanner
                      meta={resolveApprovedQuoteBannerMeta({
                        approvedAt: timberQuoteApprovedAt(project ?? {}, sub.id),
                        initials: timberQuoteApproverInitials(project ?? {}, sub.id),
                        bill: findAwardBillForSubmission(supplierBills, project?.id, sub.id),
                        dealId: deal.id,
                      })}
                      onEditPaymentTerms={
                        user?.role !== 'staff' && project?.id && sub.id
                          ? () => {
                              const awardBill = findAwardBillForSubmission(
                                supplierBills,
                                project.id,
                                sub.id,
                              );
                              setPaymentTermsTarget(
                                buildApprovedQuotePaymentTermsTarget({
                                  submissionKey: sub.id!,
                                  vendorName: orgName,
                                  quoteLabel: `${label} — ${orgName}`,
                                  quoteAmount: amountValue,
                                  bill: awardBill,
                                }),
                              );
                            }
                          : undefined
                      }
                    />
                  )}
                  {isRejected && (
                    <div className="srfq-timber-quote-row-banner srfq-timber-quote-row-banner--rejected">
                      Rejected quote
                    </div>
                  )}
                  <div className="other-quotes-row-body">
                    <div className="other-quotes-row-primary">
                      <div className="other-quotes-row-identity">
                        <span className="other-quotes-vendor-name">{orgName}</span>
                        {amountValue != null && (
                          <span className="other-quotes-amount">{formatQuoteTotal(amountValue)}</span>
                        )}
                      </div>
                      <div className="other-quotes-row-actions">
                        <button
                          type="button"
                          className="other-quotes-action-btn other-quotes-action-btn--open"
                          onClick={() => onOpenSubmission(sub)}
                        >
                          <ExternalLink size={13} aria-hidden />
                          Open
                        </button>
                        {(showLegacyApprove || showPoReview) && user?.role !== 'staff' && project?.id && sub.id && (
                          <button
                            type="button"
                            className="other-quotes-action-btn other-quotes-action-btn--approve"
                            disabled={approveBusyId === sub.id}
                            onClick={() => handleApproveQuote(sub, orgId, orgName)}
                          >
                            <CheckCircle2 size={13} aria-hidden />
                            {showPoReview
                              ? canReviewPo && isAwaitingReviewer(linkedPo, user?.id)
                                ? 'Approve'
                                : 'Review PO'
                              : 'Approve'}
                          </button>
                        )}
                        {linkedPo && (
                          <Link
                            to={`/purchasing?pr=${linkedPo.id}`}
                            className="other-quotes-action-btn other-quotes-action-btn--open"
                          >
                            {linkedPo.poNumber || linkedPo.prNumber}
                          </Link>
                        )}
                        {linkedPo?.poNumber
                          && isAchPurchasePayment({
                            id: linkedPo.paymentMethodId,
                            label: linkedPo.paymentMethod,
                          })
                          && deal.id && (
                          <Link
                            to={`/billing-payables?deal=${deal.id}`}
                            className="other-quotes-action-btn other-quotes-action-btn--open"
                          >
                            Payable
                          </Link>
                        )}
                        {isApproved && !sub.purchaseRequisitionId && user?.role !== 'staff' && project?.id && sub.id && (
                          <button
                            type="button"
                            className="other-quotes-action-btn other-quotes-action-btn--unapprove"
                            onClick={() => {
                              void onUpdateProject(
                                project.id!,
                                buildOtherQuoteApprovalUpdate(project, releaseId, sub.id!, false),
                              );
                              void retractSupplierBillForRfqAward({
                                project,
                                submissionKey: sub.id!,
                                byUserId: user?.id || '',
                                byUserName: user?.name || user?.email || '',
                              });
                            }}
                          >
                            <RotateCcw size={13} aria-hidden />
                            Undo approval
                          </button>
                        )}
                        {canReject && (
                          <button
                            type="button"
                            className="other-quotes-action-btn other-quotes-action-btn--reject"
                            onClick={() =>
                              openRejectionModal(
                                rejectionTargets.filter((t) => t.orgId === orgId),
                              )
                            }
                          >
                            <MailX size={13} aria-hidden />
                            Reject
                          </button>
                        )}
                        {onDeleteSubmission && sub.id && (
                          <button
                            type="button"
                            className="other-quotes-action-btn other-quotes-action-btn--delete"
                            disabled={deleteBusyId === sub.id}
                            onClick={() => requestDeleteSubmission(sub, orgName)}
                          >
                            <Trash2 size={13} aria-hidden />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="other-quotes-row-meta">
                      <span className="other-quotes-label-badge">{label}</span>
                      {nodeLabel && <span className="other-quotes-label-badge">{nodeLabel}</span>}
                      {linkedPo && (
                        <span className="other-quotes-po-badge">
                          {linkedPo.poNumber || linkedPo.prNumber}
                          {' · '}
                          {PURCHASE_STATUS_LABEL[linkedPo.status]}
                        </span>
                      )}
                      {payLabel && <span className="other-quotes-pay-badge">Pay by {payLabel}</span>}
                      {linkedPo?.status === 'approved'
                        && isAchPurchasePayment({
                          id: linkedPo.paymentMethodId,
                          label: linkedPo.paymentMethod,
                        }) && (
                        <span className="other-quotes-pay-badge">Pay in Billing &amp; Payables</span>
                      )}
                      {linkedPo?.status === 'approved'
                        && !isAchPurchasePayment({
                          id: linkedPo.paymentMethodId,
                          label: linkedPo.paymentMethod,
                        }) && (
                        <span className="other-quotes-pay-badge">Mark purchased on Purchasing</span>
                      )}
                      {validUntil && (
                        <span className="srfq-other-quote-valid-until">Valid until {validUntil}</span>
                      )}
                      {sub.notes?.trim() && (
                        <span className="srfq-timber-quote-notes" title={sub.notes.trim()}>
                          {sub.notes.trim()}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {uploadOpen && deal.id && (
        <OtherQuoteUploadModal
          open
          deal={deal}
          timberProject={timberProject}
          project={project}
          packages={packages}
          onClose={() => setUploadOpen(false)}
          onSuccess={(result) => {
            if (result.issued && result.paymentMethodId === 'ach_office') {
              setPaymentTermsTarget({
                submissionKey: result.submissionId,
                vendorName: result.vendorName,
                quoteLabel: result.quoteLabel,
                amount: result.amount,
                purchaseRequisitionId: result.purchaseRequisitionId,
                initialTerms: result.paymentTermsDraft ?? null,
              });
            }
          }}
        />
      )}

      <RfqQuoteDeleteConfirmModal
        open={!!deleteTarget}
        target={
          deleteTarget
            ? {
                quoteLabel: formatOtherQuoteLabel(deleteTarget.sub),
                vendorName: deleteTarget.orgName,
                amount: submissionQuoteTotal(deleteTarget.sub),
              }
            : null
        }
        busy={!!deleteBusyId}
        error={deleteError}
        onClose={() => {
          if (!deleteBusyId) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={() => void handleDeleteSubmission()}
      />

      <OtherQuoteApprovalModal
        open={!!approvalTarget}
        target={approvalTarget}
        dealId={deal.id}
        requisition={
          approvalTarget?.sub.purchaseRequisitionId
            ? linkedPos[approvalTarget.sub.purchaseRequisitionId]
            : null
        }
        canApprovePo={
          !!approvalTarget?.sub.purchaseRequisitionId
          && !!user?.id
          && !!linkedPos[approvalTarget.sub.purchaseRequisitionId]
          && (isAwaitingReviewer(linkedPos[approvalTarget.sub.purchaseRequisitionId]!, user.id)
            || canOverride)
        }
        canReturnPo={
          !!approvalTarget?.sub.purchaseRequisitionId
          && !!user?.id
          && !!linkedPos[approvalTarget.sub.purchaseRequisitionId]
          && (isAwaitingReviewer(linkedPos[approvalTarget.sub.purchaseRequisitionId]!, user.id)
            || canOverride)
        }
        canOverride={canOverride}
        onClose={() => setApprovalTarget(null)}
        onConfirmLegacy={handleConfirmApproval}
        onApprovePo={input => {
          if (!approvalTarget) return Promise.resolve();
          return approveLinkedPurchaseOrder(approvalTarget, input);
        }}
        onReturnPo={note => {
          if (!approvalTarget) return Promise.resolve();
          return returnLinkedPurchaseOrder(approvalTarget, note);
        }}
      />

      <SupplierPaymentTermsModal
        open={!!paymentTermsTarget}
        vendorName={paymentTermsTarget?.vendorName ?? ''}
        quoteLabel={paymentTermsTarget?.quoteLabel ?? ''}
        billAmount={paymentTermsTarget?.amount ?? 0}
        projectId={project?.timberProjectId || timberProject?.id || undefined}
        initialTerms={paymentTermsTarget?.initialTerms ?? null}
        lockedInstallmentIds={paymentTermsTarget?.lockedInstallmentIds}
        paidInstallmentIds={paymentTermsTarget?.paidInstallmentIds}
        onSave={handleSavePaymentTerms}
        onClose={() => setPaymentTermsTarget(null)}
      />

      {project && rejectionModalOpen && rejectionModalTargets && (
        <SupplierQuoteRejectionModal
          open
          targets={rejectionModalTargets}
          project={project}
          workType="other_quote"
          projectNumber={projectNumber}
          projectName={projectName}
          supplierOrgById={supplierOrgById}
          supplierInvites={supplierInvites}
          onClose={() => {
            setRejectionModalOpen(false);
            setRejectionModalTargets(null);
            const next = rejectionAfterCloseRef.current;
            rejectionAfterCloseRef.current = null;
            next?.();
          }}
        />
      )}
    </section>
  );
};

export default OtherQuotesPanel;
